import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ActionTypes } from '../sockets/ActionTypes';
import { useSocketAction } from './useSocketManager';

/**
 * Hook to listen for real-time page updates
 * @param {string} workspaceId - The workspace ID to filter events
 */
const usePageSocket = (workspaceId) => {
  const dispatch = useDispatch();
  const { user } = useSelector(state => state.user);
  const { byId } = useSelector(state => state.pages);

  useSocketAction([
    ActionTypes.PAGE_CREATED,
    ActionTypes.PAGE_UPDATED,
    ActionTypes.PAGE_DELETED,
    ActionTypes.PAGE_ARCHIVED,
    ActionTypes.PAGE_MOVED,
    ActionTypes.PAGE_REORDERED,
    ActionTypes.PAGE_CONTENT_UPDATED,
    ActionTypes.PAGE_MEMBER_ADDED,
    ActionTypes.PAGE_MEMBER_UPDATED,
    ActionTypes.PAGE_MEMBER_REMOVED,
    ActionTypes.PAGE_FAVORITED,
    ActionTypes.PAGE_VISIBILITY_CHANGED,
  ], (data) => {
    // Only process events for the current workspace
    if (data.workspaceId !== workspaceId) return;

    // Skip events triggered by current user (already handled optimistically)
    if (data.reqUser === user?.user?._id) return;

    console.log('[PageSocket] Real-time event:', data.action, data);

    // Handle different action types
    switch (data.action) {
      case ActionTypes.PAGE_CREATED:
        // Add new page to the store
        if (data.page) {
          dispatch({
            type: 'pages/addPage',
            payload: data.page
          });
        }
        break;

      case ActionTypes.PAGE_UPDATED:
      case ActionTypes.PAGE_CONTENT_UPDATED:
        // Update existing page in the store
        if (data.page) {
          dispatch({
            type: 'pages/updatePageFromSocket',
            payload: data.page
          });
        }
        break;

      case ActionTypes.PAGE_DELETED:
        // Remove page from the store
        if (data.pageId) {
          dispatch({
            type: 'pages/removePage',
            payload: data.pageId
          });
        }
        break;

      case ActionTypes.PAGE_ARCHIVED:
        // Update archived status
        if (data.page) {
          dispatch({
            type: 'pages/updatePageFromSocket',
            payload: data.page
          });
        }
        break;

      case ActionTypes.PAGE_MOVED:
      case ActionTypes.PAGE_REORDERED:
        // Update page order/parent
        if (data.page) {
          dispatch({
            type: 'pages/updatePageFromSocket',
            payload: data.page
          });
        }
        break;

      case ActionTypes.PAGE_MEMBER_ADDED:
      case ActionTypes.PAGE_MEMBER_UPDATED:
      case ActionTypes.PAGE_MEMBER_REMOVED:
        // Update page members
        if (data.page) {
          dispatch({
            type: 'pages/updatePageFromSocket',
            payload: data.page
          });
        }
        break;

      case ActionTypes.PAGE_FAVORITED:
        // Update favorite status
        if (data.page) {
          dispatch({
            type: 'pages/updatePageFromSocket',
            payload: data.page
          });
        }
        break;

      case ActionTypes.PAGE_VISIBILITY_CHANGED:
        // Update visibility
        if (data.page) {
          dispatch({
            type: 'pages/updatePageFromSocket',
            payload: data.page
          });
        }
        break;

      default:
        console.warn('[PageSocket] Unknown action type:', data.action);
    }
  });
};

export default usePageSocket;

